import {
    useComposedRef,
    useEditorPlugin,
    useEditorReadOnly,
    useFocused,
    useOnClickOutside,
    usePluginOption,
} from '@udecode/plate/react';
import {
    getDOMSelectionBoundingClientRect,
    type UseVirtualFloatingOptions,
} from '@udecode/plate-floating';
import React from 'react';

import { CitationPlugin } from '../../CitationPlugin';
import { submitFloatingCitation } from '../../transforms';
import { useFloatingCitationEnter } from './useFloatingCitationEnter';
import { useFloatingCitationEscape } from './useFloatingCitationEscape';
import { useVirtualFloatingCitation } from './useVirtualFloatingCitation';

export type CitationFloatingToolbarState = {
  floatingOptions?: UseVirtualFloatingOptions;
};

export const useFloatingCitationInsertState = ({
    floatingOptions,
}: CitationFloatingToolbarState = {}) => {
    const { editor } = useEditorPlugin(CitationPlugin);

    const readOnly = useEditorReadOnly();
    const focused = useFocused();
    const isOpen = usePluginOption(CitationPlugin, 'isOpen', editor.id);

    const floating = useVirtualFloatingCitation({
        editorId: editor.id,
        getBoundingClientRect: getDOMSelectionBoundingClientRect,
        open: isOpen,
        whileElementsMounted: () => () => {},
        ...floatingOptions,
    });

    return {
        floating,
        focused,
        isOpen,
        readOnly
    };
};

export const useFloatingCitationInsert = ({
    floating,
    isOpen,
    readOnly,
}: ReturnType<typeof useFloatingCitationInsertState>) => {
    const { api, editor, setOption, getOption } = useEditorPlugin(CitationPlugin);

    const ref = useOnClickOutside(
        () => {
            submitFloatingCitation(editor);
            api.floatingCitation.hide();
            editor.tf.focus({ at: editor.selection! });
        },
        {
            disabled: !isOpen,
        }
    );

    const editCallback = getOption('editCitationCallback');
    const pageNumberPrefix = getOption('pageNumberPrefix');

    // wait for update before focusing input
    React.useEffect(() => {
        if (isOpen) {
            floating.update();
            setOption('updated', true);
        } else {
            setOption('updated', false);
        }
    }, [isOpen, floating.update]);

    useFloatingCitationEscape();
    useFloatingCitationEnter();

    return {
        hidden: readOnly || !isOpen,
        props: {
            style: {
                ...floating.style,
                zIndex: 50,
            },
        },
        ref: useComposedRef<HTMLDivElement>(floating.refs.setFloating, ref),
        editCallback,
        pageNumberPrefix
    };
};
